import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

export type FileStatus = {
  filename: string
  status: 'pending' | 'uploading' | 'validating' | 'uploaded' | 'error'
  progress: number
}

type UploadingState = {
  uploading: boolean
  fileStatuses: FileStatus[]
  currentDocumentId: string | null
  currentProjectId: string | null
  currentTag: string | null
  abortController: AbortController
}

type UploadingActions = {
  setUploading: (uploading: boolean) => void
  setFileStatuses: (
    fileStatus: FileStatus[] | ((prev: FileStatus[]) => FileStatus[]),
  ) => void
  updateFileStatus: (filename: string, updates: Partial<FileStatus>) => void
  initializeFileStatuses: (files: File[]) => void
  clearFileStatuses: () => void
  setCurrentDocumentId: (currentDocumentId: string) => void
  setCurrentProjectId: (currentProjectId: string) => void
  reset: () => void
}

type UploadingSelectors = {
  isAllFilesUploadedOrFailed: () => boolean
}

type UploadingStore = UploadingState & UploadingActions & UploadingSelectors

const initialState: UploadingState = {
  uploading: false,
  fileStatuses: [],
  currentDocumentId: null,
  currentProjectId: null, // Note: This value does not tie to the projectId in the selection dropdown in the GenerateDocumentDialog. This value is set when the user clicks start uploading, and is used by the request for uploading the SOP.
  currentTag: null,
  abortController: new AbortController(),
}

export const useUploadingStore = create<UploadingStore>()(
  immer((set, get) => ({
    ...initialState,
    setUploading: (uploading: boolean) =>
      set((state) => {
        state.uploading = uploading
      }),
    setFileStatuses: (
      fileStatus: FileStatus[] | ((prev: FileStatus[]) => FileStatus[]),
    ) =>
      set((state) => {
        state.fileStatuses =
          typeof fileStatus === 'function'
            ? fileStatus(state.fileStatuses)
            : fileStatus
      }),
    updateFileStatus: (filename: string, updates: Partial<FileStatus>) =>
      set((state) => {
        const index = state.fileStatuses.findIndex(
          (status) => status.filename === filename,
        )
        if (index !== -1) {
          state.fileStatuses[index] = {
            ...state.fileStatuses[index],
            ...updates,
          }
        }
      }),
    initializeFileStatuses: (files: File[]) =>
      set((state) => {
        state.fileStatuses = files.map((file) => ({
          filename: file.name,
          status: 'pending' as const,
          progress: 0,
        }))
        state.abortController = new AbortController()
      }),
    clearFileStatuses: () =>
      set((state) => {
        state.fileStatuses = []
      }),
    setCurrentDocumentId: (currentDocumentId: string) =>
      set((state) => {
        state.currentDocumentId = currentDocumentId
      }),
    setCurrentProjectId: (currentProjectId: string | null) =>
      set((state) => {
        state.currentProjectId = currentProjectId
      }),
    reset: () =>
      set((_state) => ({
        ...initialState,
        // NOTE: Need to reset the abort controller otherwise the previous abort controller will be used for the new upload
        abortController: new AbortController(),
      })),
    isAllFilesUploadedOrFailed: () => {
      const { fileStatuses } = get()
      return (
        fileStatuses.length > 0 &&
        fileStatuses.every(
          (fileStatus) =>
            fileStatus.status === 'uploaded' || fileStatus.status === 'error',
        )
      )
    },
  })),
)
